// @soft-depth
import React, { useState } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CircleHelp } from 'lucide-react';
import ConstellationIcon from '../ConstellationIcon';
import { getConstellation } from '../constellation';
import { SlideTransition } from '../../Transition/Transition';
import SoftDepthScope from '../../../design-system/SoftDepthScope';
import LegacyThemeScope from '../../../design-system/LegacyThemeScope';
import { SetBirthdayLink } from './SetBirthdayPicker';

/**
 * 星座, 生日號數 and 靈魂數 read from a date of birth. Smart IDEA shows them on
 * its 性格類型 card; 招募 IDEA on the candidate's persona card, with the rest
 * of 「他是誰」. Takes the birthday and how to set one, never the store: each
 * host saves it its own way.
 *
 * Soft Depth tiles (business mode, in 招募 too). The 「?」 explanations are
 * the legacy centred dialog they always were, kept on the legacy theme.
 */

type IntlLike = { formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string };

export interface DobReadingsProps {
  intl: IntlLike;
  dob?: string | Date | null;
  /** Where 「先設定生日」 goes. Without it the hint is plain text. */
  onSetBirthday?: () => void;
  /** Replaces the 「先設定生日」 hint entirely, e.g. with an inline date picker. */
  renderBirthdayHint?: () => React.ReactNode;
  className?: string;
}

const calculateSoulNumber = (birthdate: string): number => {
  let num = birthdate
    .replace(/[^0-9]/g, '')
    .split('')
    .reduce((sum, digit) => sum + Number.parseInt(digit, 10), 0);
  while (num > 9) {
    num = num
      .toString()
      .split('')
      .reduce((sum, digit) => sum + Number.parseInt(digit, 10), 0);
  }
  return num;
};

const HelpDialog = (props: { open: boolean; onClose: () => void; intl: IntlLike; titleId: string; bodyId: string }) => (
  <LegacyThemeScope>
    <Dialog
      open={props.open}
      onClose={props.onClose}
      keepMounted
      style={{ zIndex: 2000 }}
      slots={{ transition: SlideTransition }}
    >
      <DialogTitle>{props.intl.formatMessage({ id: props.titleId })}</DialogTitle>
      <DialogContent>{props.intl.formatMessage({ id: props.bodyId })}</DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color="primary">
          {props.intl.formatMessage({ id: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  </LegacyThemeScope>
);

/** One reading: a small grey tile, its title above the text. */
const ReadingTile = (props: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <Box sx={{ p: 1.25, px: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {props.icon}
      <Typography component="span" variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        {props.title}
      </Typography>
    </Stack>
    <Typography component="div" variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
      {props.children}
    </Typography>
  </Box>
);

const DobReadings = (props: DobReadingsProps) => {
  const { intl, dob, onSetBirthday, renderBirthdayHint, className } = props;
  const [birthdayHelpOpen, setBirthdayHelpOpen] = useState(false);
  const [soulHelpOpen, setSoulHelpOpen] = useState(false);
  const t = (id: string) => intl.formatMessage({ id });

  const dobMoment = dob ? moment(dob) : undefined;
  const constellation = getConstellation(dobMoment);
  const birthdayNumber = dobMoment ? dobMoment.format('D') : 0;
  const soulNumber = dobMoment ? calculateSoulNumber(dobMoment.format('YYYY-MM-DD')) : 0;

  const setBirthdayHint = renderBirthdayHint ? (
    renderBirthdayHint()
  ) : (
    <SetBirthdayLink label={t('set-birthday-first')} onClick={onSetBirthday} />
  );

  const helpButton = (label: string, onClick: () => void) => (
    <IconButton size="small" aria-label={label} onClick={onClick} sx={{ p: 0.25, m: -0.25, color: 'text.secondary' }}>
      <CircleHelp size={16} aria-hidden="true" />
    </IconButton>
  );

  return (
    <SoftDepthScope mode="business">
      <Stack className={className} data-testid="dob-readings" sx={{ gap: 1.5 }}>
        <HelpDialog
          open={birthdayHelpOpen}
          onClose={() => setBirthdayHelpOpen(false)}
          intl={intl}
          titleId="birthday-number"
          bodyId="birthday-number-introduction"
        />
        <HelpDialog
          open={soulHelpOpen}
          onClose={() => setSoulHelpOpen(false)}
          intl={intl}
          titleId="soul-number"
          bodyId="soul-number-introduction"
        />

        {constellation && (
          <ReadingTile
            icon={
              <ConstellationIcon src={constellation.image} className={undefined} style={{ width: 18, height: 18 }} />
            }
            title={`${t('cstlt')}:`}
          >
            {t(constellation.descIntl)}
          </ReadingTile>
        )}

        <ReadingTile
          icon={helpButton(t('birthday-number'), () => setBirthdayHelpOpen(true))}
          title={`${t('birthday-number')}:`}
        >
          {dobMoment ? t(`birthday-number-${birthdayNumber}-description`) : setBirthdayHint}
        </ReadingTile>

        <ReadingTile icon={helpButton(t('soul-number'), () => setSoulHelpOpen(true))} title={`${t('soul-number')}:`}>
          {dobMoment ? t(`soul-number-${soulNumber}-description`) : setBirthdayHint}
        </ReadingTile>
      </Stack>
    </SoftDepthScope>
  );
};

export default DobReadings;
